import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString, WWCCIconDisplay, WWCCStyleDisplay, WWCCTextDisplay } from '../../utils/index';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import moment from 'moment';
import 'moment/locale/en-au';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { Tooltip } from 'reactstrap';

// COMPONENTS
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import StatusIcon from '../../components/StatusIcon';
import DisplayDateTime from '../../components/DisplayDateTime';

// ICONS
import { ReactComponent as InfoIcon } from '../../assets/svg/Information.svg';

// TYPES
import {
	RequestsToJoinReviewCertificationCertificationReview,
	VisitorsFetchVisitor,
	WorkingWithChildrenCheckSingleStatusCheckCommand,
	WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult
} from '../../api/models';
import { closeModalType } from '../../components/GlobalModal';

interface ChildrenCheckModalTypes {
	closeModal?: closeModalType;
	documentId?: string;
	canUpdate?: boolean;
	getVisitorsProfileData?: () => void;
	profileData?: VisitorsFetchVisitor;
	profileId?: string;
}

const ChildrenCheckModal: React.FC<ChildrenCheckModalTypes> = ({
	closeModal,
	documentId,
	canUpdate,
	getVisitorsProfileData,
	profileData,
	profileId
}) => {
	// Get all states from Redux store
	const statesList = useAppSelector((state) => state?.lists.states);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Set up Form Data state and initial Data
	const initialFormData = {
		id: undefined,
		certificationType: undefined,
		reviewType: undefined,
		displayName: undefined,
		date: undefined,
		expiry: undefined,
		state: undefined,
		country: undefined,
		certificationNumber: undefined,
		confirmed: undefined,
		cited: undefined,
		attachments: []
	};

	const [formData, setFormData] = React.useState<RequestsToJoinReviewCertificationCertificationReview>(initialFormData);

	const getDocumentData = async () => {
		setIsLoading(true);

		const currentProfileId = profileData?.id || profileId || '';
		const response = await apibridge.getRequestsTojoinReviewCertification(documentId, currentProfileId);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = initialFormData } = response;

			setIsLoading(false);

			setFormData(result);
		}
	};

	React.useEffect(() => {
		if (documentId) {
			getDocumentData();
		}

		setIsLoading(false);
	}, []);

	// Submit form data to API
	const submitFormData = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		e.preventDefault();

		setIsLoading(true);

		if (canUpdate) {
			const data = {
				certificationReviewId: formData?.id,
				cited: formData.cited,
				confirmed: formData.confirmed
			};

			const response = await apibridge.confirmDocumentsCited(data);

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);

				if (closeModal) {
					closeModal();
				}

				if (getVisitorsProfileData) {
					getVisitorsProfileData();
				}
			}
		} else {
			setIsLoading(false);

			if (closeModal) {
				closeModal();
			}
		}
	};

	// ===== WWCC API Logic =====
	const [WWCCTooltipOpen, setWWCCTooltipOpen] = React.useState<boolean>(false);
	const toggleWWCCTooltip = () => setWWCCTooltipOpen(!WWCCTooltipOpen);

	// Initial State for WWCC Status
	const initialWWCCStatus: WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult = {
		isSuccess: false,
		eligibleToWork: formData.wwccEligibleToWork,
		message: formData.wwccMessage || '',
		expiryDate: '',
		checkDate: formData.wwccLastChecked || ''
	};

	// Main State for WWCC Status
	const [WWCCStatus, setWWCCStatus] =
		React.useState<WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult>(initialWWCCStatus);

	// State to manage WWCC Status functions
	const [isLoadingWWCCStatusCheck, setIsLoadingWWCCStatusCheck] = React.useState<boolean>(false);
	const [showWWCCStatusError, setShowWWCCStatusError] = React.useState<boolean>(false);

	// Set WWCC Status when formData changes (but preserve expiry)
	React.useEffect(() => {
		setWWCCStatus((prev) => ({
			...prev,
			eligibleToWork: formData.wwccEligibleToWork,
			message: formData.wwccMessage || '',
			checkDate: formData.wwccLastChecked || '',
			// Don't override expiryDate from API response
			expiryDate: prev.expiryDate
		}));
	}, [formData.wwccEligibleToWork, formData.wwccMessage, formData.wwccLastChecked]);

	// Function to check WWCC Status
	const confirmCheckWWWCStatus = async () => {
		setIsLoadingWWCCStatusCheck(true);
		setShowWWCCStatusError(false);

		const submitData: WorkingWithChildrenCheckSingleStatusCheckCommand = {
			certificationReviewId: formData.id || '',
			state: formData.state || '',
			referenceNumber: formData.certificationNumber || '',
			surname: profileData?.lastName || ''
		};

		const response = await apibridge.postWorkingwithchildrencheckSinglestatuscheck(submitData);

		if (response.isError) {
			setShowWWCCStatusError(true);
		} else {
			// Type assertion after checking isError is false
			const successResponse = response as { result?: WorkingWithChildrenCheckSingleStatusCheckStatusCheckResult };
			if (successResponse.result) {
				const result = successResponse.result;
				const newExpiryDate = result.expiryDate;

				if (newExpiryDate) {
					setFormData((prevState) => ({
						...prevState,
						expiry: newExpiryDate
					}));
				}

				setWWCCStatus(result);
			}
		}

		setIsLoadingWWCCStatusCheck(false);
	};

	return (
		<div className="document-form-modal">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<form>
								<div className="form-group">
									<label htmlFor="state">State or Territory</label>

									<input
										placeholder="State"
										title="State"
										name="state"
										className="form-control"
										readOnly
										value={
											formData.state && formData.state !== ''
												? statesList?.filter((state) => state.value === formData.state?.toLowerCase())[0].label
												: ''
										}
									/>
								</div>

								<div className="row">
									<div className="form-group col-6">
										<label htmlFor="certificationNumber">Number</label>

										<input className="form-control" type="text" readOnly value={formData.certificationNumber} />
									</div>

									<div className="form-group col-6">
										<label htmlFor="expiry">Expiry</label>

										<input
											className="form-control"
											readOnly
											value={moment(formData.expiry || '')
												.locale('en-au')
												.format('L')}
										/>
									</div>
								</div>

								{/* WWCC API */}
								{formData.certificationType === 'WorkingWithChildrenCheck' &&
								formData.wwccCanPerformSingleStatusCheck ? (
									<>
										<div className="general-divider"></div>

										<div className="row">
											<div className="col-4">
												<div className="d-flex align-items-center fw-medium text-grey-4">Status</div>
											</div>

											<div className="col-8">
												<div className="d-flex gap-2 align-items-center justify-content-start">
													<StatusIcon statusType={WWCCIconDisplay(WWCCStatus.eligibleToWork)} width={24} height={24} />

													<p className={`mb-0 fw-medium ${WWCCStyleDisplay(WWCCStatus.eligibleToWork)}`}>
														Eligible to work: {WWCCTextDisplay(WWCCStatus.eligibleToWork)}
													</p>

													{WWCCStatus.message ? (
														<>
															<InfoIcon className="info-icon mt-0" id="wwcc-description-tooltip" />

															<Tooltip
																placement="top"
																isOpen={WWCCTooltipOpen}
																autohide={false}
																target="wwcc-description-tooltip"
																toggle={toggleWWCCTooltip}
															>
																<div className="tooltip-content">{WWCCStatus.message}</div>
															</Tooltip>
														</>
													) : null}
												</div>
											</div>

											<div className="col-4">
												<div className="d-flex align-items-center h-100 fw-medium text-grey-4">Last check</div>
											</div>

											<div className="col-4 pr-0">
												<div className="d-flex align-items-center h-100">
													<span className={`fw-medium ${WWCCStyleDisplay(WWCCStatus.eligibleToWork)}`}>
														{WWCCStatus.checkDate ? (
															<DisplayDateTime format="DD/MM/YYYY, hh:mma">{WWCCStatus.checkDate}</DisplayDateTime>
														) : (
															<>Not checked yet</>
														)}
													</span>
												</div>
											</div>

											<div className="col-4">
												<div className="check-status-wrapper">
													{isLoadingWWCCStatusCheck ? (
														<div className="d-flex align-items-center gap-2">
															<span className="text-grey-5 font-weight-bold">Processing...</span>

															<LoadingSpinner show={true} isStatic width={18} height={18} />
														</div>
													) : (
														<button className="btn-secondary btn-check-status" onClick={confirmCheckWWWCStatus}>
															Check Status
														</button>
													)}
												</div>
											</div>

											{showWWCCStatusError && (
												<div className="col-12">
													<p className="body-small fw-medium text-alert-warning mt-2 mb-0 text-right">
														Something went wrong.
													</p>
													<p className="body-small fw-medium text-alert-warning mb-0 text-right">
														Please try again in a few minutes
													</p>
												</div>
											)}
										</div>

										<div className="general-divider"></div>
									</>
								) : null}

								{/* Attachments */}
								<div className="form-group">
									<label htmlFor="createAttachments">Attachments</label>

									{formData.attachments?.map((file, index) => {
										return (
											<div key={file.id + '_' + index} className="download-link">
												<span className="link-name">{file.fileName ? file.fileName : file.id}</span>

												<a href={file.link} download={true} className="link-text d-none d-lg-block">
													Download
												</a>
											</div>
										);
									})}
								</div>

								{canUpdate && (
									<div className="form-group">
										<label className="form-control-checkbox">
											<input
												type="checkbox"
												checked={formData.confirmed}
												onChange={() =>
													setFormData((prevState) => ({
														...prevState,
														confirmed: !formData.confirmed
													}))
												}
											/>
											I have checked with the Working With Children Check authority and confirmed that this person has a
											valid and current WWCC and is authorised to work or volunteer with children.
										</label>
									</div>
								)}

								<Button
									disabled={isLoading}
									type="submit"
									onClick={(e) => submitFormData(e)}
									className="btn-primary full-width"
								>
									{canUpdate ? 'Save and Close' : 'Close'}
								</Button>
							</form>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default ChildrenCheckModal;
